// src/components/reports/DateRangeSelector.js

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker, { registerLocale } from 'react-datepicker';
import { de } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { useReportContext } from '../../contexts/ReportContext';

// Register German locale for react-datepicker.
// English is the default and requires no registration.
registerLocale('de', de);

// Generate localized month names using the browser-native Intl API.
// Returns all 12 months for the given language without any hardcoded strings.
const getLocalizedMonths = (language) => {
  const locale = language === 'de' ? 'de-DE' : 'en-US';
  return Array.from({ length: 12 }, (_, i) => {
    const date = new Date(2000, i, 1);
    const label = new Intl.DateTimeFormat(locale, { month: 'long' }).format(date);
    // Capitalize first letter for consistency across browser environments
    return {
      value: i,
      label: label.charAt(0).toUpperCase() + label.slice(1)
    };
  });
};

// Generate year options from current year down to 2020
const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear; y >= 2020; y--) {
    years.push(y);
  }
  return years;
};

/**
 * Component for selecting report date range.
 * Uses react-datepicker for a polished calendar UI replacing
 * the previous native browser date inputs.
 *
 * Provides two date pickers (Startdatum / Enddatum) with range highlighting,
 * and a month/year shortcut below that auto-fills the full range for the
 * selected month. Manually selecting a date resets the shortcut dropdowns.
 *
 * Required translation keys in both en/reports.json and de/reports.json:
 *   "dateRange", "startDate", "endDate", "orSelectMonth", "month", "year"
 */
const DateRangeSelector = () => {
  const { t, i18n } = useTranslation('reports');
  const { startDate, setStartDate, endDate, setEndDate } = useReportContext();

  // Shortcut dropdown state - empty string means no active selection
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear,  setSelectedYear]  = useState('');

  // ─── Helpers ────────────────────────────────────────────────────────────────

  // Set startDate and endDate to the first and last day of the given month/year.
  // Day 0 of the next month resolves to the last day of the current month,
  // correctly handling months with 28, 29, 30, or 31 days.
  const applyMonthYear = (month, year) => {
    const firstDay = new Date(year, month, 1);
    firstDay.setHours(0, 0, 0, 0);

    const lastDay = new Date(year, month + 1, 0);
    lastDay.setHours(23, 59, 59, 999);

    setStartDate(firstDay);
    setEndDate(lastDay);
  };

  // ─── DatePicker change handlers ──────────────────────────────────────────────
  // react-datepicker passes a Date object directly (not a DOM event).
  // A new Date copy is created to avoid mutating the picker's internal reference.

  const handleStartDateChange = (date) => {
    if (!date) return;
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);

    // If new start exceeds current end, push end forward to match
    if (newDate > endDate) {
      const adjustedEnd = new Date(newDate);
      adjustedEnd.setHours(23, 59, 59, 999);
      setEndDate(adjustedEnd);
    }

    setStartDate(newDate);

    // Manual pick invalidates the shortcut selection
    setSelectedMonth('');
    setSelectedYear('');
  };

  const handleEndDateChange = (date) => {
    if (!date) return;
    const newDate = new Date(date);
    newDate.setHours(23, 59, 59, 999);

    setEndDate(newDate);

    // Manual pick invalidates the shortcut selection
    setSelectedMonth('');
    setSelectedYear('');
  };

  // ─── Shortcut dropdown handlers ──────────────────────────────────────────────

  const handleMonthChange = (e) => {
    const newMonth = e.target.value;
    setSelectedMonth(newMonth);

    // Auto-apply only when both fields carry a value
    if (newMonth !== '' && selectedYear !== '') {
      applyMonthYear(parseInt(newMonth, 10), parseInt(selectedYear, 10));
    }
  };

  const handleYearChange = (e) => {
    const newYear = e.target.value;
    setSelectedYear(newYear);

    // Auto-apply only when both fields carry a value
    if (selectedMonth !== '' && newYear !== '') {
      applyMonthYear(parseInt(selectedMonth, 10), parseInt(newYear, 10));
    }
  };

  // ─── Derived data ────────────────────────────────────────────────────────────

  // Re-derived on every render so language switches are reflected immediately
  const months      = getLocalizedMonths(i18n.language);
  const yearOptions = getYearOptions();

  // Pass undefined for English so react-datepicker uses its built-in default
  const activeLocale = i18n.language === 'de' ? 'de' : undefined;

  // ─── Shared props applied to both DatePicker instances ───────────────────────

  const sharedPickerProps = {
    startDate,
    endDate,
    dateFormat:       'dd.MM.yyyy',
    locale:           activeLocale,
    calendarStartDay: 1,                       // Start week on Monday, matching iOS app
    showPopperArrow:  false,                   // Cleaner popup without connector arrow
    popperProps:      { strategy: 'fixed' },   // Prevents clipping inside overflow containers
    calendarClassName: 'klokify-calendar',
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="date-range-selector">

      <div className="date-range-title">{t('dateRange')}</div>

      {/* ── Primary date pickers ── */}
      <div className="date-range-container">
        <div className="date-picker-group">
          <label htmlFor="start-date-picker">{t('startDate')}</label>
          <DatePicker
            {...sharedPickerProps}
            id="start-date-picker"
            selected={startDate}
            onChange={handleStartDateChange}
            selectsStart
            className="date-picker"
          />
        </div>

        <div className="date-picker-group">
          <label htmlFor="end-date-picker">{t('endDate')}</label>
          <DatePicker
            {...sharedPickerProps}
            id="end-date-picker"
            selected={endDate}
            onChange={handleEndDateChange}
            selectsEnd
            minDate={startDate}
            className="date-picker"
          />
        </div>
      </div>

      {/* ── Shortcut divider ── */}
      <div className="month-year-divider">
        <span className="month-year-divider-label">
          {t('orSelectMonth', { defaultValue: 'oder Monat auswählen' })}
        </span>
      </div>

      {/* ── Month / Year shortcut dropdowns ── */}
      <div className="month-year-container">
        <div className="month-year-group">
          <label htmlFor="month-select">
            {t('month', { defaultValue: 'Monat' })}
          </label>
          <select
            id="month-select"
            value={selectedMonth}
            onChange={handleMonthChange}
            className="month-year-select"
          >
            <option value="">
              {t('month', { defaultValue: 'Monat' })}
            </option>
            {months.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div className="month-year-group">
          <label htmlFor="year-select">
            {t('year', { defaultValue: 'Jahr' })}
          </label>
          <select
            id="year-select"
            value={selectedYear}
            onChange={handleYearChange}
            className="month-year-select"
          >
            <option value="">
              {t('year', { defaultValue: 'Jahr' })}
            </option>
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

    </div>
  );
};

// ─── Exported date formatter ────────────────────────────────────────────────────
// Used by other report components to display dates in DD.MM.YY format (iOS-matching)
export const formatReportDate = (date) => {
  const day   = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year  = String(date.getFullYear()).substring(2);
  return `${day}.${month}.${year}`;
};

export default DateRangeSelector;